import { useState, useEffect, useCallback } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { MessageSquare } from "lucide-react";
import { toast } from "sonner";

export function VisitorCommentsDialog({ bookingId, visitorName, onClose, onSaved }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!bookingId) return;
    api.get(`/bookings/${bookingId}`).then(({ data }) => setComments(data.comments || [])).catch(() => {});
  }, [bookingId]);
  useEffect(() => { load(); }, [load]);

  if (!bookingId) return null;

  const save = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.post(`/bookings/${bookingId}/comments`, { text });
      setText("");
      toast.success("Comment added");
      load();
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add comment");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!bookingId} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="visitor-comments-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-[#1B4332]" /> Comments — {visitorName}
          </DialogTitle>
          <DialogDescription>Internal notes about this visitor's stay. Visible to staff only.</DialogDescription>
        </DialogHeader>
        <div className="max-h-64 overflow-y-auto space-y-2">
          {comments.length === 0 && <p className="text-sm text-[#7A8A84]" data-testid="no-comments">No comments yet.</p>}
          {comments.slice().reverse().map((c) => (
            <div key={c.id} className="rounded-lg border border-[#E8E8E6] bg-[#FAFAF8] p-2.5">
              <p className="text-sm text-[#1C1F1E] whitespace-pre-wrap">{c.text}</p>
              <p className="text-[10px] text-[#7A8A84] mt-1">{c.by_name} · {c.at ? new Date(c.at).toLocaleString() : ""}</p>
            </div>
          ))}
        </div>
        <div className="space-y-2">
          <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)}
                    placeholder="e.g. Prefers ground floor, vegetarian meals…" data-testid="comment-input" />
          <Button onClick={save} disabled={saving || !text.trim()} className="w-full bg-[#1B4332] hover:bg-[#2D6A4F]"
                  data-testid="comment-save-btn">
            {saving ? "Saving…" : "Add comment"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
